import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Loader2, Handshake } from "lucide-react";
import { toast } from "sonner";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import SEO from "../components/SEO";
import SocialAuthButtons from "../components/SocialAuthButtons";
import { getStoredRef, captureRefFromURL } from "../lib/refCapture";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [ref, setRef] = useState(null);

  useEffect(() => {
    // If we arrived with ?ref= in URL, persist it; then use whatever we have.
    captureRefFromURL();
    setRef(getStoredRef());
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    try {
      const u = await register(name, email, password, ref);
      toast.success(`Conta criada! Bem-vindo, ${u.name}!`);
      navigate("/feed");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Erro ao cadastrar");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center">
      <SEO title="Criar conta" path="/cadastro" noindex />
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-primary flex items-center justify-center mb-3">
            <Sparkles className="w-7 h-7 text-primary-foreground" />
          </div>
          <h1 className="font-heading font-bold text-2xl">Criar conta</h1>
          <p className="text-sm text-muted-foreground">Descubra João Pessoa com IA</p>
        </div>
        {ref && (
          <div data-testid="register-ref-banner" className="mb-4 rounded-2xl bg-primary/10 border border-primary/30 p-3 text-xs flex items-center gap-2">
            <Handshake className="w-4 h-4 text-primary shrink-0" />
            <span>Você foi indicado por um <b>Guia Comercial</b> (código <code className="bg-white/60 px-1.5 py-0.5 rounded">{ref}</code>).</span>
          </div>
        )}
        <form onSubmit={submit} className="space-y-3">
          <input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)}
            placeholder="Nome" className="w-full h-12 px-4 rounded-2xl bg-card border border-border focus:ring-2 focus:ring-primary outline-none" />
          <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="E-mail" className="w-full h-12 px-4 rounded-2xl bg-card border border-border focus:ring-2 focus:ring-primary outline-none" />
          <input data-testid="register-password" type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="Senha (mín. 6 caracteres)" className="w-full h-12 px-4 rounded-2xl bg-card border border-border focus:ring-2 focus:ring-primary outline-none" />
          {error && <p data-testid="register-error" className="text-sm text-alert">{error}</p>}
          <button data-testid="register-submit" type="submit" disabled={loading}
            className="w-full h-12 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center disabled:opacity-60 active:scale-95 transition-transform">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Criar conta"}
          </button>
        </form>

        <div className="flex items-center gap-3 my-6">
          <div className="h-px flex-1 bg-border" />
          <span className="text-xs text-muted-foreground uppercase tracking-wider">ou cadastre-se com</span>
          <div className="h-px flex-1 bg-border" />
        </div>
        <SocialAuthButtons />
        <p className="text-center text-sm text-muted-foreground mt-5">
          Já tem conta? <Link data-testid="register-to-login" to="/login" className="text-primary font-semibold">Entrar</Link>
        </p>
      </motion.div>
    </div>
  );
}
